import type { ReactNode } from 'react';

export interface SummaryRow {
  readonly key: ReactNode;
  readonly value: ReactNode;
  /** Bold this row (used for "Total" or final emphasis). */
  readonly emphasis?: boolean;
}

export interface SummaryProps {
  readonly rows: readonly SummaryRow[];
  readonly className?: string;
}

/**
 * Key/value summary card used in Send review and Miner vesting blocks.
 *
 * Visually similar to ListCard but with secondary text on the left
 * side instead of primary, and no clickable rows. Used purely for
 * displaying calculated values.
 */
export function Summary({ rows, className = '' }: SummaryProps) {
  return (
    <div
      className={[
        'rounded-xl bg-surface-1 px-4',
        '[&>*+*]:border-t [&>*+*]:border-border-hairline',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid grid-cols-[1fr_auto] items-center gap-3 py-3"
        >
          <div
            className={[
              'text-xs',
              row.emphasis
                ? 'font-semibold text-text-primary'
                : 'text-text-secondary',
            ].join(' ')}
          >
            {row.key}
          </div>
          <div
            className={[
              'text-right text-[13px]',
              row.emphasis
                ? 'font-semibold text-text-primary'
                : 'font-medium text-text-primary',
            ].join(' ')}
          >
            {row.value}
          </div>
        </div>
      ))}
    </div>
  );
}
